import pAll from 'p-all';
import { defineStore, acceptHMRUpdate } from 'pinia';
import {
  deepLinkParser,
  type DeeplinkRedirect,
} from '~/lib/category/deepLinkParser';
export const useFeedStore = defineStore('FeedStore', () => {
  if (!useAppRunningStore().isRunning) return;

  const { $db } = useNuxtApp();

  const feeds = ref<FeedSchema[]>([]);

  const mDeeplinks = new Map<string, DeeplinkRedirect>();

  const contents = useContents();

  const _retrieveMedias = (_feeds: FeedSchema[], _medias: MediaSchema[]) => {
    const oMedias = _medias.reduce((acc, media) => {
      acc[media.id] = media;
      return acc;
    }, {});
    _feeds.forEach((feed) => {
      feed.iconMedia = oMedias[feed.iconMediaId];
    });
    return _feeds;
  };

  const _retrieveDeeplinks = async (_feeds: FeedSchema[]) => {
    const deepLinkActions = [];
    _feeds.forEach((feed) => {
      if (feed.actions && feed.actions.length) {
        feed.actions.forEach((action) => {
          if (action.deeplink) {
            if (mDeeplinks.get(action.deeplink)) {
              action.deeplinkRedirect = mDeeplinks.get(action.deeplink);
            } else {
              deepLinkActions.push(async () => {
                if (!mDeeplinks.get(action.deeplink)) {
                  const redirect = await deepLinkParser(
                    $db,
                    action.deeplink,
                    contents.all.value,
                    contents.mainContent.value,
                    false
                  );
                  mDeeplinks.set(action.deeplink, redirect);
                }
                action.deeplinkRedirect = mDeeplinks.get(action.deeplink);
              });
            }
          }
        });
      }
    });
    await pAll(deepLinkActions, { concurrency: 2 });
    // filter deeplinks
    return _feeds.reduce((acc, feed) => {
      if (feed.actions && feed.actions.length > 0) {
        const hasNotManagedDeeplink = feed.actions.some((action) => {
          if (!action.deeplink) return false;
          if (action.deeplinkRedirect && action.deeplinkRedirect.path)
            return false;
          return true;
        });
        if (hasNotManagedDeeplink) {
          if (feed.fallbackMessage || feed.fallbackTitle) {
            feed.message = feed.fallbackMessage
              ? feed.fallbackMessage
              : feed.message;
            feed.title = feed.fallbackTitle ? feed.fallbackTitle : feed.title;
            delete feed.actions;
          } else {
            return acc;
          }
        }
      }
      acc.push(feed);
      return acc;
    }, []);
  };

  const setData = async (_feeds: FeedSchema[], _medias: MediaSchema[]) => {
    _feeds = _retrieveMedias(_feeds, _medias);
    _feeds = await _retrieveDeeplinks(_feeds);
    feeds.value = _feeds;
  };

  const newFeedsCount = computed(() => {
    return feeds.value.filter((feed) => !feed.read).length;
  });

  const newUserFeedsCount = computed(() => {
    return feeds.value.filter((feed) => !feed.read && feed.type === 'self')
      .length;
  });

  const newFollowingFeedsCount = computed(() => {
    return feeds.value.filter((feed) => !feed.read && feed.type === 'following')
      .length;
  });

  const totalCount = computed(() => {
    return feeds.value.length;
  });

  return {
    setData,
    feeds,
    newFeedsCount,
    newUserFeedsCount,
    newFollowingFeedsCount,
    totalCount,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFeedStore, import.meta.hot));
}
